import React, { useState, useEffect, useCallback } from "react";
import type { GuestOfferView, OfferItem } from "../features/offers/hooks/useOfferDrawer";

/** Payload of the /api/offers guest-view / confirm responses. */
interface GuestOfferResponse {
  offer?: GuestOfferView;
  status?: "pending" | "already_confirmed" | "expired" | "sent" | "error";
  bookingId?: string;
  error?: string;
  /** Human-readable failure reason (e.g. the availability shortage) from the server. */
  details?: string;
}
import { useTranslation } from "react-i18next";
import {
  FileText,
  Loader2,
  AlertCircle,
  CheckCircle,
  Building2,
  CalendarDays,
  Users,
  Euro,
  Check,
  Clock,
  Sparkles,
  Mail,
  RefreshCw
} from "lucide-react";

/**
 * Public confirmation view shown to guests when reviewing/accepting an offer.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.tenantId - The target tenant identifier context.
 * @param {string} props.offerId - The ID of the offer to confirm.
 * @returns {JSX.Element} The offer acceptance confirmation screen.
 */
export default function PublicOfferConfirmation({ tenantId, offerId }: { tenantId: string; offerId: string }) {
  const { t, i18n } = useTranslation();
  const [offerData, setOfferData] = useState<GuestOfferView | null>(null);
  const [status, setStatus] = useState<"loading" | "pending" | "already_confirmed" | "expired" | "sent" | "rejected" | "error">("loading"); // loading, pending, already_confirmed, expired, rejected, error
  const [offerError, setOfferError] = useState("");
  const [isConfirming, setIsConfirming] = useState(false);
  const [bookingId, setBookingId] = useState("");
  const [viewMode, setViewMode] = useState<"summary" | "detail">("summary");
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Capture the confirmation token from the URL fragment exactly once, during
  // the first render. App.jsx runs useFilterParam hooks unconditionally, and
  // their mount effect rewrites the URL via setSearchParams({ replace: true }),
  // which strips the `#token=...` hash fragment. Reading the token here — before
  // any effect runs — guarantees the fragment is still intact.
  const [token] = useState(() => {
    const hash = window.location.hash.replace(/^#/, "");
    const params = new URLSearchParams(hash);
    return params.get("token") || "";
  });

  const loadOffer = useCallback(async () => {
    if (!token || !offerId || !tenantId) {
      setStatus("error");
      setOfferError(t("offerConfirmation.invalidLink"));
      return;
    }

    try {
      // Bypass HTTP/browser cache so the latest cut-off date (validUntil)
      // and any other server-side mutations are always picked up.
      const res = await fetch(`/api/offers?action=guest-view&t=${Date.now()}`, {
        method: "POST",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
          "Pragma": "no-cache"
        },
        body: JSON.stringify({ offerId, confirmationToken: token, tenantId })
      });

      const data: GuestOfferResponse = await res.json();
      if (!res.ok) {
        setStatus("error");
        setOfferError(data.error || t("offerConfirmation.loadError"));
        if (data.offer) setOfferData(data.offer ?? null);
        return;
      }

      setOfferData(data.offer ?? null);
      setStatus(data.status || "pending");

      if (data.status === "already_confirmed") {
        setBookingId(data.bookingId || "");
      }
    } catch (err) {
      console.error("Fehler beim Laden des Angebots:", err);
      setStatus("error");
      setOfferError(t("offerConfirmation.connectionError"));
    }
  }, [offerId, tenantId, token, t]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await loadOffer();
    } finally {
      setIsRefreshing(false);
    }
  }, [loadOffer]);

  useEffect(() => {
    loadOffer();
  }, [loadOffer]);

  // The offer carries its own language (de|en): render the guest page in THAT
  // language, not the browser's — the staff member chose it per offer.
  const offerLanguage = offerData?.language === "en" ? "en" : "de";
  useEffect(() => {
    if (offerData && i18n.language !== offerLanguage) {
      i18n.changeLanguage(offerLanguage);
    }
  }, [offerData, offerLanguage, i18n]);
  const locale = offerLanguage === "en" ? "en-GB" : "de-DE";

  // Re-fetch the offer whenever the tab regains focus so the guest always
  // sees the freshest cut-off date / status after the host edits them.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        loadOffer();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [loadOffer]);

  const handleConfirm = async () => {
    if (!token || !offerId || !tenantId) return;

    setIsConfirming(true);
    try {
      const res = await fetch("/api/offers?action=confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offerId, confirmationToken: token, tenantId })
      });

      const data: GuestOfferResponse = await res.json();
      if (!res.ok) throw new Error([data.error, data.details].filter(Boolean).join(": ") || t("offerConfirmation.confirmationFailed"));

      setStatus("already_confirmed");
      setBookingId(data.bookingId || "");
      setOfferData((prev) => (prev ? { ...prev, status: "converted" } : prev));
    } catch (err) {
      setOfferError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-white flex items-center justify-center p-4 md:p-6 font-sans relative overflow-hidden">
      <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-[#859F4A]/10 rounded-full blur-[100px] animate-pulse duration-[10s]" />
      <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-indigo-500/10 rounded-full blur-[100px] animate-pulse duration-[8s]" />

      <div className="relative w-full max-w-2xl">
        {status === "loading" && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl p-14 text-center">
            <Loader2 className="animate-spin mx-auto text-[#859F4A] mb-6" size={40} />
            <p className="text-sm font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">{t("offerConfirmation.loading")}</p>
          </div>
        )}

        {status === "error" && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl p-10 md:p-14 text-center space-y-5">
            <div className="w-16 h-16 bg-rose-50 dark:bg-rose-950/40 rounded-2xl flex items-center justify-center text-rose-500 border border-rose-200 dark:border-rose-800/60 mx-auto">
              <AlertCircle size={36} />
            </div>
            <h2 className="text-2xl font-black display-font uppercase tracking-tight text-slate-900 dark:text-white">
              {t("offerConfirmation.notAvailable")}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">{offerError}</p>
            {offerData && offerData.status === "expired" && (
              <div className="p-4 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 rounded-2xl text-amber-700 dark:text-amber-300 text-sm">
                {t("offerConfirmation.expired", {
                  date: new Date(offerData.validUntil || "").toLocaleString(locale, {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                  })
                })}
              </div>
            )}
          </div>
        )}

        {status === "already_confirmed" && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl p-10 md:p-14 text-center space-y-6 animate-in zoom-in">
            <div className="w-20 h-20 bg-emerald-50 dark:bg-emerald-950/40 rounded-2xl flex items-center justify-center text-emerald-500 border border-emerald-200 dark:border-emerald-800/60 mx-auto">
              <CheckCircle size={48} />
            </div>
            <h2 className="text-2xl md:text-3xl font-black display-font uppercase tracking-tight text-slate-900 dark:text-white">
              {bookingId ? t("offerConfirmation.bookingConfirmed") : t("offerConfirmation.alreadyConfirmed")}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-lg mx-auto">
              {bookingId
                ? t("offerConfirmation.thankYouMessage", { bookingId })
                : t("offerConfirmation.alreadyConverted")}
            </p>
            <div className="inline-flex items-center gap-2 px-5 py-3 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800/60 rounded-xl text-indigo-600 dark:text-indigo-400 text-sm font-bold">
              <CheckCircle size={18} />
              {t("offerConfirmation.bookingId")}: {bookingId || offerData?.offerNumber}
            </div>
          </div>
        )}

        {(status === "pending" || status === "sent") && offerData && (
          <div className="space-y-6 animate-in slide-in-from-bottom-4">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl p-6 md:p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#859F4A] rounded-2xl flex items-center justify-center text-white shadow-lg shrink-0">
                  <FileText size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl md:text-2xl font-black display-font uppercase tracking-tight text-slate-900 dark:text-white">
                    {t("offerConfirmation.yourOffer")}
                  </h1>
                  <p className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mt-0.5">
                    {offerData.offerNumber} &middot; {t("offerConfirmation.validUntil")} {new Date(offerData.validUntil || "").toLocaleString(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-[#859F4A] transition-all disabled:opacity-50 shrink-0"
                  title={t("offerConfirmation.refresh")}
                  aria-label={t("offerConfirmation.refresh")}
                >
                  <RefreshCw size={16} className={isRefreshing ? "animate-spin" : ""} />
                </button>
              </div>

              {offerData.offerItems && offerData.offerItems.length > 0 && (
                <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl mb-6">
                  <button
                    onClick={() => setViewMode("summary")}
                    className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-lg transition-all ${viewMode === "summary" ? "bg-white dark:bg-slate-900 shadow-xs text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"}`}
                  >
                    {t("offerConfirmation.summary")}
                  </button>
                  <button
                    onClick={() => setViewMode("detail")}
                    className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-lg transition-all ${viewMode === "detail" ? "bg-white dark:bg-slate-900 shadow-xs text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"}`}
                  >
                    {t("offerConfirmation.details")}
                  </button>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                  <Building2 size={18} className="text-[#859F4A] shrink-0" />
                  <div>
                    <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t("offerConfirmation.location")}</p>
                    <p className="text-sm font-bold text-slate-900 dark:text-white">{offerData.propertyId || "Adapt Apartments"}</p>
                  </div>
                </div>

                {(!offerData.offerItems || offerData.offerItems.length === 0 || viewMode === "summary") && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                        <CalendarDays size={18} className="text-[#859F4A] shrink-0" />
                        <div>
                          <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t("offerConfirmation.arrival")}</p>
                         <p className="text-sm font-bold text-slate-900 dark:text-white">{new Date(offerData.arrival || "").toLocaleDateString(locale)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                        <CalendarDays size={18} className="text-[#859F4A] shrink-0" />
                        <div>
                          <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t("offerConfirmation.departure")}</p>
                          <p className="text-sm font-bold text-slate-900 dark:text-white">{new Date(offerData.departure || "").toLocaleDateString(locale)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                        <Users size={18} className="text-[#859F4A] shrink-0" />
                        <div>
                          <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t("offerConfirmation.guests")}</p>
                          <p className="text-sm font-bold text-slate-900 dark:text-white">
                            {offerData.adults} {t("offerConfirmation.adults")}
                            {(offerData.children ?? 0) > 0 ? `, ${offerData.children} ${t("offerConfirmation.children")}` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                        <Building2 size={18} className="text-[#859F4A] shrink-0" />
                        <div>
                          <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t("offerConfirmation.services", "Leistungen")}</p>
                          <p className="text-sm font-bold text-slate-900 dark:text-white">
                            {offerData.offerItems && offerData.offerItems.length > 0
                              ? `${offerData.offerItems.reduce((a, b) => a + Number(b.quantity), 0)} ${t("offerConfirmation.servicesCount", "Leistungen")} ${t("offerConfirmation.inPositions", "in")} ${offerData.offerItems.length} ${t("offerConfirmation.positions", "Positionen")}`
                              : offerData.unitTypeName || t("offerConfirmation.notSpecified")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {offerData.offerItems && offerData.offerItems.length > 0 && viewMode === "detail" && (
                  <div className="space-y-3 bg-slate-50 dark:bg-slate-800/60 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                    <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3">{t("offerConfirmation.servicesBreakdown")}</p>
                    {offerData.offerItems.map((item: OfferItem, idx: number) => (
                      <div key={idx} className="flex justify-between items-center border-b border-slate-200 dark:border-slate-700 pb-2 last:border-0 last:pb-0">
                        <div>
                          <p className="text-sm font-bold text-slate-900 dark:text-white">{item.quantity}x {item.name} {item.type === "room" ? `(${item.ratePlanName})` : ""}</p>
                          <p className="text-[10px] text-slate-500 dark:text-slate-400">
                            {item.pricePerUnit === '' ? '' : item.pricePerUnit.toFixed(2)} EUR {item.type === "room" ? t("groupsList.perNight") : t("offerConfirmation.perUnit")}
                          </p>
                        </div>
                        <span className="font-black text-slate-900 dark:text-white">{item.total?.toFixed(2)} EUR</span>
                      </div>
                    ))}
                    {(offerData.cityTax ?? 0) > 0 && (
                      <div className="flex justify-between items-center border-t border-slate-200 dark:border-slate-700 pt-2 mt-2">
                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400">{t("offerConfirmation.cityTaxIncludedShort")} (7.5%)</span>
                        <span className="text-xs font-black text-slate-500 dark:text-slate-400">{offerData.cityTax?.toFixed(2)} EUR</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between p-5 bg-[#859F4A]/10 dark:bg-[#859F4A]/15 rounded-2xl border border-[#859F4A]/20">
                  <div>
                    <p className="text-[9px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">{t("offerConfirmation.totalPrice")}</p>
                    <p className="text-2xl font-black text-slate-900 dark:text-white">
                      {offerData.price?.total?.toFixed(2)} {offerData.price?.currency || "EUR"}
                    </p>
                  </div>
                  <Euro size={32} className="text-[#859F4A] opacity-40" />
                </div>

                {offerData.guestMessage && (
                  <div className="p-4 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800/60 rounded-2xl">
                    <p className="text-[9px] font-black text-indigo-700 dark:text-indigo-400 uppercase tracking-widest mb-1">{t("offerConfirmation.teamMessage")}</p>
                    <p className="text-sm text-indigo-900 dark:text-indigo-200">{offerData.guestMessage}</p>
                    </div>
                  )}

                  {/* Standard expiry notice (always shown, in the offer's language): makes
                      the automatic cut-off behavior explicit to the guest. */}
                  <div className="p-4 bg-accent-muted/30 border border-border rounded-2xl">
                    <p className="text-[9px] font-black text-muted uppercase tracking-widest mb-1">{t("offerConfirmation.expiryNoticeTitle")}</p>
                    <p className="text-sm text-fg">{t("offerConfirmation.expiryNotice")}</p>
                  </div>

                {offerData.companyName && (
                  <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                    <Building2 size={18} className="text-[#859F4A] shrink-0" />
                    <div>
                      <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t("offerConfirmation.company")}</p>
                      <p className="text-sm font-bold text-slate-900 dark:text-white">{offerData.companyName}</p>
                    </div>
                  </div>
                )}

                {offerData.cancellationPolicy && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 rounded-2xl">
                    <p className="text-[9px] font-black text-amber-700 dark:text-amber-400 uppercase tracking-widest mb-1">{t("offerConfirmation.cancellationPolicy")}</p>
                    <p className="text-sm text-amber-900 dark:text-amber-200">{offerData.cancellationPolicy}</p>
                  </div>
                )}
              </div>
            </div>

            {offerError && (
              <div className="p-4 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 rounded-2xl flex items-center gap-3">
                <AlertCircle size={18} className="text-rose-500 shrink-0" />
                <p className="text-sm text-rose-600 dark:text-rose-400 font-medium">{offerError}</p>
              </div>
            )}

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl p-6 md:p-8">
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleConfirm}
                  disabled={isConfirming}
                  className="flex-1 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-lg flex items-center justify-center gap-3 disabled:opacity-50 cursor-pointer"
                >
                  {isConfirming ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <Check size={20} />
                  )}
                  {isConfirming ? t("offerConfirmation.booking") : t("offerConfirmation.acceptAndBook")}
                </button>
                <a
                  href={`mailto:${offerData?.contactEmail || "support@adapthub.com"}?subject=${encodeURIComponent("Angebot " + offerData.offerNumber)}`}
                  className="flex-1 py-4 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 font-black text-[11px] uppercase tracking-widest rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all text-center flex items-center justify-center gap-3"
                >
                  <Mail size={20} />
                  {t("offerConfirmation.questions")}
                </a>
              </div>
              <p className="text-[9px] text-slate-400 dark:text-slate-500 text-center mt-4">
                {t("offerConfirmation.termsAccept")}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
